import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Camera, X, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { ImageLightbox } from './ImageLightbox';
import { AuthImage } from './AuthImage';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SEC-002.6 (2026-08-17): the `buildImageUrl` helper was DELETED. It produced
// unauthenticated `<img src>` URLs that always 401'd against the dtoken-only
// `/api/files` endpoint. Every caller has been migrated to `<AuthImage path=…>`
// (which mints a 60s dtoken lazily via `useDownloadUrl`) or to
// `<ImageLightbox images=[{path, alt}]>` (same dtoken path). If you find
// yourself reaching for a synchronous URL builder for an authed image — you
// don't need one; use `AuthImage`.

// Image upload button for breakdown items
export const ImageUploadButton = ({ itemId, onUploaded, disabled }) => {
  const { token } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/breakdown-items/${itemId}/images`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Photo uploaded');
      onUploaded && onUploaded(response.data.storage_path);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileRef}
        onChange={handleUpload}
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid={`image-upload-input-${itemId}`}
      />
      <Button
        size="sm"
        variant="ghost"
        onClick={() => fileRef.current?.click()}
        disabled={disabled || uploading}
        className="h-7 w-7 p-0 text-[#a0a0a0] hover:text-[#e8c547]"
        title="Add photo"
        data-testid={`upload-image-btn-${itemId}`}
      >
        <Camera className="h-3 w-3" />
      </Button>
    </>
  );
};

// A clickable thumbnail that opens images in a lightbox.
// If `galleryImages` is supplied, the lightbox shows that wider gallery and
// initialIndex is `galleryStartIndex` (so arrow keys navigate across all
// items in a section). Otherwise it falls back to this item's own paths.
export const LightboxThumb = ({ paths, alt = '', className = 'h-8 w-8 object-cover', galleryImages, galleryStartIndex = 0 }) => {
  const [open, setOpen] = useState(false);

  if (!paths || paths.length === 0) return null;

  const gallery = galleryImages && galleryImages.length
    ? galleryImages
    : paths.map((p, i) => ({ path: p, alt: `${alt} ${i + 1}` }));
  const initial = galleryImages && galleryImages.length ? galleryStartIndex : 0;

  return (
    <>
      <AuthImage
        path={paths[0]}
        alt={alt}
        className={`${className} cursor-zoom-in`}
        data-testid="lightbox-thumb"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      />
      {open && (
        <ImageLightbox
          images={gallery}
          initialIndex={initial}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

// Inline image preview with hover effect.
// If `galleryImages` is supplied, clicking any thumb opens the lightbox
// at that thumb's position within the wider section gallery.
export const ImageThumbStrip = ({ paths, itemId, onDelete, galleryImages, galleryStartIndex = 0 }) => {
  const { token } = useAuth();
  const [hoveredPath, setHoveredPath] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  if (!paths || paths.length === 0) return null;

  const handleDelete = async (path, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this photo?')) return;
    try {
      await axios.delete(`${API}/breakdown-items/${itemId}/images?path=${encodeURIComponent(path)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo removed');
      onDelete && onDelete(path);
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  return (
    <div className="flex gap-2 flex-wrap mt-1" data-testid={`image-strip-${itemId}`}>
      {paths.map((path, idx) => (
        <div
          key={path}
          className="relative group/img"
          onMouseEnter={() => setHoveredPath(path)}
          onMouseLeave={() => setHoveredPath(null)}
        >
          <AuthImage
            path={path}
            alt={`reference ${idx + 1}`}
            className="h-12 w-12 object-cover rounded border border-[#2a2a2a] cursor-pointer"
            data-testid={`image-thumb-${itemId}-${idx}`}
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(idx); }}
          />
          {onDelete && (
            <button
              type="button"
              onClick={(e) => handleDelete(path, e)}
              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
              data-testid={`delete-image-${itemId}-${idx}`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          )}
          {hoveredPath === path && (
            <div className="absolute bottom-full left-0 mb-2 z-50 pointer-events-none" data-testid="image-preview">
              <AuthImage
                path={path}
                alt="preview"
                className="max-w-xs max-h-64 object-contain rounded border-2 border-[#e8c547] shadow-2xl bg-[#0d0d0d]"
              />
            </div>
          )}
        </div>
      ))}

      {lightboxIdx !== null && (
        <ImageLightbox
          images={
            galleryImages && galleryImages.length
              ? galleryImages
              : paths.map((p, i) => ({ path: p, alt: `Image ${i + 1}` }))
          }
          initialIndex={
            galleryImages && galleryImages.length
              ? galleryStartIndex + lightboxIdx
              : lightboxIdx
          }
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
};

// Inline "📷" indicator that shows image on hover (used in table cells)
export const ImageHoverIndicator = ({ paths }) => {
  const [hovered, setHovered] = useState(false);

  if (!paths || paths.length === 0) return null;

  return (
    <span
      className="relative inline-flex items-center gap-1 ml-1 cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="image-hover-indicator"
    >
      <ImageIcon className="h-3 w-3 text-[#e8c547] inline" />
      <span className="text-xs text-[#e8c547]">{paths.length}</span>
      {hovered && (
        <div className="absolute bottom-full left-0 mb-2 z-50 pointer-events-none flex gap-1">
          {paths.slice(0, 3).map((path, idx) => (
            <AuthImage
              key={path}
              path={path}
              alt={`preview ${idx + 1}`}
              className="max-w-[150px] max-h-32 object-contain rounded border-2 border-[#e8c547] shadow-2xl bg-[#0d0d0d]"
            />
          ))}
        </div>
      )}
    </span>
  );
};
